import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../redux/store";
import {
  fetchPasswordSetupStatus,
  getEmployeeProfile,
  updateEmployeeProfile,
  clearError,
  clearSuccessMessage,
} from "../../redux/features/profile";
import { X, Loader2 } from "lucide-react";
import { toast } from "react-toastify";
import Avatar from "../../components/common/ngwAvatar";
import { UpdateEmployeeProfileRequest } from "../../types/request";

const ProfileManagement: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  
  // Get user data from Redux state
  const user = useSelector((state: RootState) => state.user.user);
  const { employeeProfile, isLoadingProfile, isUpdatingProfile, error, successMessage } = useSelector(
    (state: RootState) => state.profile
  );

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isPasswordValid, setIsPasswordValid] = useState(true);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [isPhoneValid, setIsPhoneValid] = useState(true);

  // Get the employee ID from user state
  const employeeId = user.os_emp_account_id?.toString();

  // Fetch profile data on component mount
  useEffect(() => {
    if (employeeId && employeeId !== "0" && employeeId !== "-1") {
      dispatch(getEmployeeProfile({ employee_id: employeeId }));
    }
  }, [employeeId, dispatch]);

  // Clear error when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearError());
    };
  }, [dispatch]);

  // Display error toast if there's an error
  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  // Display success message from API response
  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccessMessage());
    }
  }, [successMessage, dispatch]);

  useEffect(() => {
    setPhoneNumber(employeeProfile?.phone || "");
  }, [employeeProfile?.phone]);

  // Check if employeeId is valid (not loading state)
  const isValidEmployeeId = employeeId && employeeId !== "0" && employeeId !== "-1";
  const sanitizedStoredPhone = (employeeProfile?.phone || "").trim();
  const sanitizedInputPhone = phoneNumber.trim();
  const isPhoneChanged = sanitizedInputPhone !== sanitizedStoredPhone;
  const isPasswordSectionDirty = password.length > 0 || confirmPassword.length > 0;

  const handleUpdateProfile = async () => {
    if (!employeeId || !isValidEmployeeId) {
      return;
    }

    if (!isPhoneChanged && !isPasswordSectionDirty) {
      toast.info("No changes to update");
      return;
    }

    const updatePayload: UpdateEmployeeProfileRequest = {
      employee_id: employeeId,
    };

    if (isPhoneChanged) {
      if (!sanitizedInputPhone) {
        setIsPhoneValid(false);
        toast.error("Phone number cannot be empty");
        return;
      }

      setIsPhoneValid(true);
      updatePayload.phone_number = sanitizedInputPhone;
    }

    if (isPasswordSectionDirty) {
      // Validate passwords match
      if (password !== confirmPassword) {
        setIsPasswordValid(false);
        toast.error("Passwords do not match");
        return;
      }

      // Validate password is not empty
      // if (!password || password.length < 6) {
      //   setIsPasswordValid(false);
      //   toast.error("Password must be at least 6 characters long");
      //   return;
      // }

      setIsPasswordValid(true);
      updatePayload.account = {
        password,
      };
    }

    // Update profile with new password / phone
    const result = await dispatch(updateEmployeeProfile(updatePayload));

    if (updateEmployeeProfile.fulfilled.match(result)) {
      // Clear password fields
      setPassword("");
      setConfirmPassword("");
      setIsPhoneValid(true);
      
      // Reload profile and setup status so the banner clears after password save.
      dispatch(getEmployeeProfile({ employee_id: employeeId }));
      dispatch(fetchPasswordSetupStatus({ employee_id: employeeId }));
    }
  };

  // Show loading state while waiting for employeeId or while loading profile
  if ((!isValidEmployeeId || isLoadingProfile) && !employeeProfile) {
    return (
      <div className="min-h-screen bg-white w-full flex items-center justify-center">
        <div className="flex flex-col items-center">
          <Loader2 className="w-8 h-8 animate-spin text-black" />
          <p className="mt-4 text-gray-600">
            {!isValidEmployeeId ? "Loading employee information..." : "Loading profile..."}
          </p>
        </div>
      </div>
    );
  }

  // Show error state only if employeeId is valid but profile failed to load
  if (!employeeProfile && !isLoadingProfile && isValidEmployeeId) {
    return (
      <div className="min-h-screen bg-white w-full flex items-center justify-center">
        <div className="text-center">
          <X className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-red-600 mb-4">Failed to load profile</p>
        </div>
      </div>
    );
  }

  // Get full name from profile
  const fullName = employeeProfile?.full_name || `${employeeProfile?.first_name || ""} ${employeeProfile?.last_name || ""}`.trim() || "Employee";
  const jobTitle = employeeProfile?.job_titles || "";

  return (
    <div className="min-h-screen bg-white w-full">
      {/* Main Container - Responsive padding and max-width */}
      <div className="w-full max-w-2xl mx-auto px-4 py-6 md:py-8">
        {/* Top Section - Profile Identification */}
        <div className="flex flex-col items-center mb-6 md:mb-8">
          {/* Profile Picture */}
          <div className="relative mb-4 p-1 rounded-full border-2 border-black">
            <Avatar 
              src={employeeProfile?.profile_image_url || null}
              name={fullName}
              className="w-24 h-24 md:w-32 md:h-32"
            />
          </div>

          {/* Name */}
          <h1 className="text-2xl md:text-3xl font-bold text-black text-center mb-2">
            {fullName}
          </h1>

          {/* Role Tag */}
          {jobTitle && (
            <div className="inline-block px-3 py-1 rounded-full bg-gray-200">
              <span className="text-sm md:text-base text-gray-700 font-normal">{jobTitle}</span>
            </div>
          )}
        </div>

        {/* Bottom Section - Contact and Account Details Card */}
        <div className="bg-white rounded-lg shadow-md p-4 md:p-6 w-full">
          {/* Email Field - Disabled */}
          <div className="mb-4 md:mb-5">
            <label 
              htmlFor="email" 
              className="block text-sm md:text-base font-normal text-gray-700 mb-1 md:mb-2"
            >
              Email
            </label>
            <input
              type="email"
              id="email"
              value={employeeProfile?.email || ""}
              readOnly
              disabled
              className="w-full px-3 py-2 md:py-2.5 border border-gray-300 rounded-lg text-sm md:text-base text-gray-900 font-normal bg-gray-100 cursor-not-allowed focus:outline-none"
            />
          </div>

          {/* Phone Field */}
          <div className="mb-4 md:mb-5">
            <label 
              htmlFor="phone" 
              className="block text-sm md:text-base font-normal text-gray-700 mb-1 md:mb-2"
            >
              Phone
            </label>
            <input
              type="tel"
              id="phone"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="Enter phone number"
              inputMode="tel"
              className={`w-full px-3 py-2 md:py-2.5 border rounded-lg text-sm md:text-base text-gray-900 font-normal bg-white focus:outline-none focus:ring-2 ${
                !isPhoneValid ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-gray-400 focus:border-transparent"
              }`}
            />
            {!isPhoneValid && (
              <p className="mt-1 text-xs text-red-500">Phone number cannot be empty.</p>
            )}
          </div>

          {/* Password Field */}
          <div className="mb-4 md:mb-5">
            <label 
              htmlFor="password" 
              className="block text-sm md:text-base font-normal text-gray-700 mb-1 md:mb-2"
            >
              New Password
            </label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter new password"
              className={`w-full px-3 py-2 md:py-2.5 border rounded-lg text-sm md:text-base text-gray-900 font-normal bg-white focus:outline-none focus:ring-2 ${
                !isPasswordValid ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-gray-400 focus:border-transparent"
              }`}
            />
          </div>

          {/* Confirm Password Field */}
          <div className="mb-4 md:mb-5">
            <label 
              htmlFor="confirmPassword" 
              className="block text-sm md:text-base font-normal text-gray-700 mb-1 md:mb-2"
            >
              Confirm New Password
            </label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder="Confirm new password"
              className={`w-full px-3 py-2 md:py-2.5 border rounded-lg text-sm md:text-base text-gray-900 font-normal bg-white focus:outline-none focus:ring-2 ${
                !isPasswordValid ? "border-red-500 focus:ring-red-400" : "border-gray-300 focus:ring-gray-400 focus:border-transparent"
              }`}
            />
          </div>

          {/* Update Profile Button */}
          <button
            onClick={handleUpdateProfile}
            disabled={isUpdatingProfile || (!isPhoneChanged && !isPasswordSectionDirty)}
            className={`w-full py-2.5 md:py-3 border-2 rounded-lg text-sm md:text-base font-medium transition-colors ${
              isUpdatingProfile || (!isPhoneChanged && !isPasswordSectionDirty)
                ? "border-gray-300 bg-gray-100 text-gray-400 cursor-not-allowed"
                : "border-blue-600 bg-transparent text-blue-600 hover:bg-blue-50 active:bg-blue-100"
            }`}
          >
            {isUpdatingProfile ? (
              <div className="flex items-center justify-center">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Updating...
              </div>
            ) : (
              "Update Profile"
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProfileManagement;
